import { action, observable } from 'mobx';
import { ViewController } from '@estee/elc-service';
import { emit, events, query, serviceQueries } from '@estee/elc-service-bus';
import { ProductRepository } from '../repositories/ProductRepository';
import { getProductIdFromUrl } from '../utils/getProductIdFromUrl';
import { getSearchParam } from '../utils/getSearchParam';
import { VTO_DISCLOSE_PARAM, VTO_FOUNDATION } from '../constants/VtoFoundationOverlay';
import { diContainer } from '../../service-setup/diContainer';
import serviceNames from '~setup/ContentService';

interface IBestMatch {
    hexVal: string;
    name: string;
}

interface IVtoFoundationPayload {
    vtoType: string;
}

export class VtoBaseViewController extends ViewController {
    @observable public bestMatch: IBestMatch;
    @observable public isVtoDisclosed: boolean = false;
    @observable public isLoading: boolean = true;
    private productRepository: ProductRepository;
    private vtoFoundationPayload: IVtoFoundationPayload = { vtoType: VTO_FOUNDATION };

    constructor() {
        super();
        this.productRepository = diContainer.get(serviceNames.productRepository);
    }

    @action
    public setIsVtoDisclosed = () => {
        const vtoDiscloseParam = getSearchParam(VTO_DISCLOSE_PARAM);
        this.isVtoDisclosed = vtoDiscloseParam !== null;
    };

    @action
    public setBestMatch = async () => {
        const bestMatchSkuId = await this.getBestMatchSkuId();

        if (!bestMatchSkuId) {
            return;
        }

        const bestMatch = await this.getShadeData(bestMatchSkuId);

        if (bestMatch?.name) {
            const { name, hexVal } = bestMatch;
            this.bestMatch = { name, hexVal };

            emit(events.VTO_SHADE_REMINDER_OVERLAY_OPENED, this.vtoFoundationPayload);
        }
    };

    public closeShadeReminderOverlay = () => {
        emit(events.VTO_SHADE_REMINDER_OVERLAY_CLOSED, this.vtoFoundationPayload);
    };

    public retainShadeReminderOverlay = () => {
        emit(events.VTO_SHADE_REMINDER_OVERLAY_RETAINED, this.vtoFoundationPayload);
    };

    public retakeVtoShadeReminder = () => {
        emit(events.VTO_SHADE_REMINDER_OVERLAY_RETAKEN, this.vtoFoundationPayload);
    };

    private async getBestMatchSkuId() {
        const productId = getProductIdFromUrl();

        if (!productId) {
            return;
        }

        return query(serviceQueries.GET_BEST_MATCH_SKU_ID, { productId });
    }

    private async getShadeData(skuId: string) {
        const sku = await this.productRepository.getSkuById(skuId);

        if (sku && sku.shades.length > 0) {
            return sku.shades[0];
        }
    }
}
